import React, { useState, useEffect } from 'react';
import { Card, Typography, Row, Col, Button, Tooltip, Divider, Modal } from 'antd';
import { useTranslation } from 'react-i18next';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import env from '../../../environment';
import { useSelector, useDispatch } from 'react-redux';
import { trace } from '../../../components/tagmanager';
import numeral from 'numeral';
import _ from 'lodash';
import { getAnalytics, logEvent } from 'firebase/analytics';
import { refreshData } from '../../../store/auth/actions';
import { refreshStages } from '../../../store/auth/actions';
import { isMobileOnly } from 'react-device-detect';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import {
	Download04Icon,
	Payment01Icon,
	Cardiogram02Icon,
	Car01Icon,
	FileEuroIcon,
	UserShield01Icon,
	House04Icon,
	StickyNote02Icon,
	SafeIcon,
	CreditCardIcon,
	CreditCardAcceptIcon,
	FirstAidKitIcon,
	ChartIncreaseIcon,
	EnergyIcon
} from 'hugeicons-react';

const { Text } = Typography;

// Icono personalizado para "Seguro de vida k constante"
const LifeInsuranceKConstanteIcon = ({ size = 24, color = 'currentColor', ...props }) => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width={size}
		height={size}
		viewBox="0 0 24 24"
		fill="none"
		stroke={color}
		strokeWidth={1.5}
		strokeLinecap="round"
		strokeLinejoin="round"
		{...props}>
		<path d="M15.2053 10.7181C13.7947 9.60121 11 7.0478 11 4.74998C11 3.23121 12.0526 2 13.5 2C14.25 2 15 2.2647 16 3.32352C17 2.2647 17.75 2 18.5 2C19.9474 2 21 3.23121 21 4.74998C21 7.0478 18.2053 9.60121 16.7947 10.7181C16.32 11.094 15.68 11.094 15.2053 10.7181Z" />
		<path d="M2 19V13C2 12.535 2 12.3025 2.05111 12.1118C2.18981 11.5941 2.59413 11.1898 3.11177 11.0511C3.30252 11 3.53501 11 4 11C4.46499 11 4.69748 11 4.88823 11.0511C5.40587 11.1898 5.81019 11.5941 5.94889 12.1118C6 12.3025 6 12.535 6 13V19C6 19.465 6 19.6975 5.94889 19.8882C5.81019 20.4059 5.40587 20.8102 4.88823 20.9489C4.69748 21 4.46499 21 4 21C3.53501 21 3.30252 21 3.11177 20.9489C2.59413 20.8102 2.18981 20.4059 2.05111 19.8882C2 19.6975 2 19.465 2 19Z" />
		<path d="M6 12H7.76845C8.58101 12 9.38511 12.165 10.132 12.4851L14.8574 14.5103C15.5506 14.8074 16 15.489 16 16.2431C16 16.9373 15.4373 17.5 14.7431 17.5H14.0986C13.3729 17.5 12.6538 17.3615 11.98 17.092L10.5 16.5" />
		<path d="M14 17.5H20.5749C21.362 17.5 22 18.138 22 18.9251C22 19.5613 21.5782 20.1205 20.9664 20.2953L15.7451 21.7871C15.2508 21.9283 14.7392 22 14.2251 22C13.7437 22 13.2645 21.9372 12.7994 21.8132L6 20" />
	</svg>
);

const LINKAGE_ITEMS = {
	insecurance: { iconComponent: Cardiogram02Icon, label: 'loan.entailments_insecurance' },
	car: { iconComponent: Car01Icon, label: 'loan.entailments_car' },
	payroll: { iconComponent: FileEuroIcon, label: 'loan.entailments_payroll' },
	unemployment: { iconComponent: UserShield01Icon, label: 'loan.entailments_unemployment' },
	home: { iconComponent: House04Icon, label: 'loan.entailments_home' },
	payments: { iconComponent: StickyNote02Icon, label: 'loan.entailments_bills' },
	pensionPlan: { iconComponent: SafeIcon, label: 'loan.entailments_pensionPlan' },
	cards: { iconComponent: CreditCardIcon, label: 'loan.entailments_cards' },
	accidents: { iconComponent: FirstAidKitIcon, label: 'loan.entailments_accidentInsurance' },
	investmentFunds: { iconComponent: ChartIncreaseIcon, label: 'loan.entailments_investmentFund' },
	energyCertification: { iconComponent: EnergyIcon, label: 'loan.entailments_energyRating' },
	protectedPayments: { iconComponent: CreditCardAcceptIcon, label: 'icon.viability2' },
	keyAccount: { iconComponent: Payment01Icon, label: 'loan.entailments_keyAccount' },
	lifeInsuranceKConstante: {
		iconComponent: LifeInsuranceKConstanteIcon,
		label: 'loan.entailments_lifeInsuranceKConstante'
	}
};

const OffersWidgetsData = ({ getBankOffers, valuesData, bankOffer, years, numberOffers }) => {
	const { t } = useTranslation();
	const hasFavorite = valuesData.some((offer) => offer.isFavorite);
	const param = useParams();
	const operations = useSelector((state) => state.auth.operations);
	const userToken = useSelector((state) => state.auth.token);
	const nameOperations =
		operations !== undefined &&
		operations.find((operation) => operation.operationId === param.operationId);
	const rol = useSelector((state) => state.auth.roles);
	const location = useLocation();
	const [loading, setLoading] = useState(false);
	const [show, setShow] = useState(false);
	const refreshDataChange = useSelector((state) => state.auth.refreshData);
	const dispatch = useDispatch();
	const changeStages = useSelector((state) => state.auth.refreshStages);
	const navigate = useNavigate();
	const analytics = getAnalytics();

	useEffect(() => {
		trace({
			location,
			userToken,
			rol,
			stage: 'oferts',
			operationName: nameOperations && nameOperations.name
		});
	}, []);

	const linkages = (bankOffer) => {
		const field = [];

		if (bankOffer.data !== undefined) {
			const [entailment] = bankOffer.data.entailments;

			if (entailment.linkages !== undefined) {
				for (const [key, { iconComponent: IconComponent, label }] of Object.entries(
					LINKAGE_ITEMS
				)) {
					const linkage = entailment.linkages[key];
					if (linkage !== undefined && linkage !== false) {
						field.push(
							<Tooltip
								placement="bottom"
								title={
									<div style={{ display: 'flex', flexDirection: 'column' }}>
										<div>{t(label)}</div>
										<div>{linkage.amount + '%' + ' - ' + linkage.price + '€'}</div>
									</div>
								}>
								<IconComponent size={24} color="#757575" />
							</Tooltip>
						);
					}
				}
			}
		}

		return field;
	};

	const clickChoose = (bankOffer) => {
		setLoading(true);
		logEvent(analytics, 'select_content', {
			content_type: 'operation.offerSelection',
			content_id: 'select.offer.button'
		});
		axios
			.post(
				`${env.api.url}/banks/bank-offer/toggle-favorite`,
				{
					bankOfferId: bankOffer.id,
					isFromHadmin: false
				},
				{ headers: { Authorization: `Bearer ${userToken}` } }
			)
			.then(() => {
				setLoading(false);
				dispatch(refreshData(!refreshDataChange));
				dispatch(refreshStages(!changeStages));
				getBankOffers();
				navigate(`/operations/${param.operationId}/appraisal`);
			});
	};

	const showModal = (bankOffer) => {
		if (bankOffer.isFavorite) {
			return Modal.confirm({
				className: 'modal-confirm-undo-favorite',
				title: <span>{t('offers.removeFavorite')}</span>,
				content: <span>{t('offers.removeFavoriteDescription')}</span>,
				onOk: () => clickChoose(bankOffer),
				okText: <span>{t('offers.accept')}</span>,
				cancelText: <span>{t('offers.cancel')}</span>,
				closable: true
			});
		} else if (numberOffers > 1 && hasFavorite && !bankOffer.isFavorite) {
			return Modal.confirm({
				className: 'modal-confirm-change-favorite',
				title: <span>{t('offers.changeOffer')}</span>,
				content: <span>{t('offers.confirmChange')}</span>,
				onOk: () => clickChoose(bankOffer),
				okText: <span>{t('offers.change')}</span>,
				cancelText: <span>{t('offers.cancel')}</span>,
				closable: true
			});
		} else {
			return clickChoose(bankOffer);
		}
	};

	const descriptionVerticalSections = ({ title, data, bankOffer, size1, size2 }) => {
		return (
			<div style={{ display: 'flex', flexDirection: 'column' }}>
				<Text
					style={{
						fontSize: size1,
						fontWeight: 400,
						color: '#757575',
						textAlign: title === 'TAE*' ? 'right' : undefined
					}}>
					{title}
				</Text>
				<Text style={{ fontSize: size2, fontWeight: 700, color: '#212121' }}>
					{data === 'Mixto'
						? data +
						  ' ' +
						  bankOffer?.data?.entailments[0]?.stages[0].to / 12 +
						  ' ' +
						  t('viability.years')
						: data}
				</Text>
			</div>
		);
	};

	const descriptionHorizontalSections = ({ title, data }) => {
		return (
			<div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
				<Text style={{ fontSize: isMobileOnly ? 12 : 16, fontWeight: 400, color: '#757575' }}>
					{t(title)}
				</Text>
				<Text style={{ fontSize: isMobileOnly ? 12 : 16, fontWeight: 700, color: '#757575' }}>
					{data}
				</Text>
			</div>
		);
	};

	const handleFileDownload = (clickOfert) => {
		axios
			.get(`${env.api.url}/v1/banks/generate-pdf-bank/${clickOfert.id}?token=${userToken}`, {
				responseType: 'blob'
			})
			.then((response) => {
				if (response) {
					const url = window.URL.createObjectURL(response.data);

					const link = document.createElement('a');
					link.href = url;
					link.setAttribute('download', `Oferta_Banco_Sabadell.pdf`);
					document.body.appendChild(link);
					link.click();
					link.parentNode.removeChild(link);
				}
			})
			.catch((error) => {
				console.log(error);
			});
	};

	const downloadFile = (id, name) => {
		const download = (dataurl, filename) => {
			const link = document.createElement('a');
			link.href = dataurl;
			link.download = filename;
			link.click();
		};
		axios
			.get(`${env.api.url}/v1/hadmin/assets/download/${id}`, {
				headers: { Authorization: `Bearer ${userToken}` }
			})
			.then((response) => {
				download(response.data.data.link, name);
			})
			.catch((error) => {
				console.error('Error downloading file:', error);
			});
	};

	return (
		<>
			<div style={{ height: isMobileOnly ? undefined : '100%', width: '100%' }}>
				{bankOffer.type === 'preOffer' && (
					<div style={{ display: 'flex', justifyContent: 'end', width: '100%' }}>
						<div
							style={{
								backgroundColor: '#F9E6FF',
								color: '#212121',
								textAlign: 'center',
								borderRadius: '4px 4px 0px 0px',
								fontSize: 16,
								fontWeight: 400,
								width: 94,
								height: 28,
								display: 'flex',
								justifyContent: 'center',
								marginTop: isMobileOnly ? 10 : -30
							}}>
							{t('offer.preOffer')}
						</div>
					</div>
				)}
				<Card
					id={bankOffer.id}
					cover={
						bankOffer.isFavorite ? (
							<div
								style={{
									padding: '8px 0px',
									backgroundColor: '#006DFF',
									color: '#fff',
									textAlign: 'center',
									borderRadius: '4px 4px 0px 0px',
									fontSize: 24,
									fontWeight: 700
								}}>
								{t('offers.favoriteOffer')}
							</div>
						) : undefined
					}
					className="card-offer"
					style={{
						boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)',
						border: bankOffer.isFavorite ? '1px solid #006DFF' : 'none',
						borderRadius: 4,
						overflow: 'hidden'
					}}>
					<div
						style={{
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center',
							gap: 8
						}}>
						{descriptionVerticalSections({
							data: bankOffer.ratio !== 'Seleccione' ? bankOffer.ratio : 'No aplica',
							bankOffer,
							size2: isMobileOnly ? 18 : 24
						})}
						<Text style={{ fontSize: isMobileOnly ? 16 : 24, color: '#757575', fontWeight: 700 }}>
							{Math.floor(years)} {t('viability.years')}
							<span style={{ fontSize: isMobileOnly ? 12 : 14, fontWeight: 400, marginLeft: 4 }}>
								({Math.floor(years * 12)} {t('viability.months')})
							</span>
						</Text>
					</div>
					<Divider
						style={{
							marginTop: isMobileOnly ? 8 : 16,
							marginBottom: isMobileOnly ? 8 : 16,
							borderColor: '#E0E0E0'
						}}
					/>
					<div style={{ display: 'flex', justifyContent: 'space-between' }}>
						{descriptionVerticalSections({
							title: t('viability.amount'),
							data: numeral(Number(bankOffer.amount)).format('0,0.00 $'),
							size1: isMobileOnly ? 12 : 16,
							size2: isMobileOnly ? 16 : 24
						})}
						{bankOffer?.data?.entailments[0]?.tae !== '' &&
							bankOffer?.data?.entailments[0]?.tae !== undefined &&
							bankOffer?.data?.entailments[0]?.tae > 0 &&
							descriptionVerticalSections({
								title: 'TAE*',
								data: numeral(Number(bankOffer?.data?.entailments[0]?.tae)).format('0.00') + ' %',
								size1: isMobileOnly ? 12 : 16,
								size2: isMobileOnly ? 16 : 24
							})}
					</div>
					<Divider
						style={{
							marginTop: isMobileOnly ? 8 : 16,
							marginBottom: isMobileOnly ? 8 : 16,
							borderColor: '#E0E0E0'
						}}
					/>
					<Row gutter={12} style={{ textAlign: 'left' }}>
						{bankOffer?.data?.entailments[0]?.stages.map((tin, index) => {
							return (
								<Col
									key={`${index}-oferts`}
									span={24}
									style={{
										display: 'flex',
										flexDirection: 'column',
										marginBottom:
											numberOffers !== 1 && bankOffer?.data?.entailments[index]?.stages.length === 1
												? 60
												: undefined
									}}>
									<div
										style={{
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'space-between'
										}}>
										<Text
											style={{
												fontSize: isMobileOnly ? 12 : 16,
												fontWeight: 400,
												color: '#757575'
											}}>
											{bankOffer.ratio === 'Fijo'
												? t('viability.monthlyPayments') + ':'
												: index === 0 && tin?.to >= 12
												? t('onboarding.quoteMoreYears', { years: tin?.to / 12 })
												: index === 0 && tin?.to === 1
												? t('onboarding.quoteFirstYear')
												: t('onboarding.quoteRestYear')}
										</Text>
										<Text
											style={{
												fontSize: isMobileOnly ? 12 : 16,
												fontWeight: 400,
												color: '#757575'
											}}>
											{tin.tin !== '' && 'TIN: '}
										</Text>
									</div>

									{bankOffer.data && (
										<div
											style={{
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'space-between'
											}}>
											<Text
												style={{
													color: '#006DFF',
													fontSize: 24,
													fontWeight: 700
												}}>
												{tin.quote === '' || tin.quote < 10
													? '-'
													: numeral(Number(tin.quote)).format('0,00.00 $')}{' '}
											</Text>
											<Text
												style={{
													fontSize: isMobileOnly ? 16 : 24,
													fontWeight: 700,
													color: '#212121'
												}}>
												{tin.rateType === 'FIXED' ? undefined : 'eur + '}
												{tin.tin === ''
													? '-'
													: numeral(Number(tin.tin)).format('0,00.00') + ' %'}{' '}
											</Text>
										</div>
									)}
								</Col>
							);
						})}
					</Row>
					<Divider
						style={{
							marginTop: isMobileOnly ? 8 : 16,
							marginBottom: isMobileOnly ? 8 : 16,
							borderColor: '#E0E0E0'
						}}
					/>
					<div gutter={12}>
						{descriptionVerticalSections({
							title: t('appraisal.nameTableEntailment'),
							data: linkages(bankOffer).length !== 0 ? linkages(bankOffer) : '-',
							size1: isMobileOnly ? 12 : 16
						})}
					</div>
					<Divider
						style={{
							marginTop: isMobileOnly ? 8 : 16,
							marginBottom: isMobileOnly ? 8 : 16,
							borderColor: '#E0E0E0'
						}}
					/>
					{!show && (
						<Button
							style={{
								display: 'flex',
								alignItems: 'center',
								boxShadow: 'none',
								border: 'none',
								margin: '0 auto'
							}}
							onClick={() => setShow(!show)}>
							<Text style={{ color: '#757575', fontWeight: 700, fontSize: isMobileOnly ? 12 : 16 }}>
								{t('promoterLoan.seeMore')}
							</Text>
							<DownOutlined style={{ color: '#757575', marginLeft: 5 }} />
						</Button>
					)}
					{show && (
						<>
							<div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10 }}>
								{descriptionHorizontalSections({
									title: 'appraisal.nameTableReview',
									data:
										bankOffer.revision === null || bankOffer.revision === 'Seleccione'
											? 'No aplica'
											: bankOffer.revision
								})}
							</div>
							<div style={{ display: 'flex', justifyContent: 'space-between' }}>
								{descriptionHorizontalSections({
									title: t('appraisal.nameTableOpeningCommission'),
									data:
										bankOffer.openFee === null
											? 'No aplica'
											: numeral(Number(bankOffer.openFee)).format('0,0.00 $')
								})}
							</div>
							<div style={{ display: 'flex', justifyContent: 'space-between' }}>
								{descriptionHorizontalSections({
									title: 'appraisal.nameTablePartialAmortization',
									data: bankOffer.partialFee === null ? 'No aplica' : bankOffer.partialFee
								})}
							</div>
							<div style={{ display: 'flex', justifyContent: 'space-between' }}>
								{descriptionHorizontalSections({
									title: 'appraisal.nameTableTotalAmortization',
									data: bankOffer.totalFee === null ? 'No aplica' : bankOffer.totalFee
								})}
							</div>
							<Divider
								style={{
									marginTop: isMobileOnly ? 8 : 16,
									marginBottom: isMobileOnly ? 8 : 16,
									borderColor: '#E0E0E0'
								}}
							/>
							<div>
								{bankOffer?.files.length > 0 &&
									bankOffer.files.map((file, index) => {
										return (
											<div
												style={{ display: 'flex', flexDirection: 'column', marginBottom: 10 }}
												key={index}>
												<div
													style={{
														display: 'flex',
														justifyContent: 'space-between',
														alignItems: 'center'
													}}>
													<Text
														style={{
															color: '#757575',
															fontSize: 12,
															maxWidth: '80%',
															whiteSpace: 'nowrap',
															overflow: 'hidden',
															textOverflow: 'ellipsis'
														}}>
														{file.name}
													</Text>
													<Button
														onClick={() => downloadFile(file.id, file.name)}
														style={{ border: 'none', padding: 0 }}>
														<Download04Icon size={14} style={{ color: '#757575' }} />
													</Button>
												</div>
											</div>
										);
									})}
							</div>
							<Divider
								style={{
									marginTop: isMobileOnly ? 8 : 16,
									marginBottom: isMobileOnly ? 8 : 16,
									borderColor: '#E0E0E0'
								}}
							/>
							<Tooltip title={bankOffer.comments.length > 340 ? bankOffer.comments : null}>
								<Text style={{ fontSize: 12, color: '#757575' }}>
									{bankOffer.comments.length > 340
										? bankOffer.comments.slice(0, 340) + '...'
										: bankOffer.comments}
								</Text>
							</Tooltip>
							<Divider
								style={{
									marginTop: isMobileOnly ? 8 : 16,
									marginBottom: isMobileOnly ? 8 : 16,
									borderColor: '#E0E0E0'
								}}
							/>
						</>
					)}

					{show && (
						<Button
							style={{
								display: 'flex',
								alignItems: 'center',
								boxShadow: 'none',
								border: 'none',
								margin: '0 auto'
							}}
							onClick={() => setShow(!show)}>
							<Text style={{ color: '#757575', fontWeight: 700, fontSize: isMobileOnly ? 12 : 16 }}>
								{t('offers.seeLess')}
							</Text>
							<UpOutlined style={{ color: '#757575', marginLeft: 5 }} />
						</Button>
					)}
					<Divider
						style={{
							marginTop: isMobileOnly ? 8 : 16,
							marginBottom: isMobileOnly ? 8 : 16,
							borderColor: '#E0E0E0'
						}}
					/>
					<div
						style={{
							display: 'flex',
							gap: 16,
							marginTop: 16,
							justifyContent: bankOffer?.files.length === 0 && 'center'
						}}>
						{Array.isArray(bankOffer?.files) &&
							bankOffer.files.some((file) => file.type === 'bank-request') && (
								<Button
									style={{
										float: 'right',
										width: '50%',
										height: 32
									}}
									className="button-secundary-gibobs"
									onClick={() => handleFileDownload(bankOffer, 'close')}>
									{t('init.download')}{' '}
								</Button>
							)}
						<Button
							className="button-primari-gibobs"
							type="primary"
							loading={loading}
							onClick={() => showModal(bankOffer)}
							style={{
								margin: '0 auto',
								width: '50%',
								height: 32,
								borderColor: bankOffer.isFavorite ? '#BE0028' : '#006DFF',
								backgroundColor: bankOffer.isFavorite ? '#BE0028' : '#006DFF'
							}}>
							{bankOffer.isFavorite ? t('offers.undoLikes') : t('offert.like')}
						</Button>
					</div>
					{bankOffer && bankOffer.type === 'preOffer' && (
						<div style={{ marginTop: 16 }}>
							<Text style={{ fontSize: 12, fontWeight: 400, color: '#212121' }}>
								{t('offer.textPreoffer') + ' '}
							</Text>

							<Text
								style={{
									fontSize: 12,
									fontWeight: 400,
									color: '#212121',
									textDecoration: 'underline'
								}}>
								{t('offer.textPreoffer2')}
							</Text>
						</div>
					)}
				</Card>
			</div>
		</>
	);
};

export default OffersWidgetsData;
